import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiArrowRight, FiCheckCircle, FiMail, FiPhone, FiX } from "react-icons/fi";
import { FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { BsInstagram } from "react-icons/bs";
import emailjs from "@emailjs/browser";

import { AppWrap, MotionWrap } from "../../wrapper";
import { client } from "../../client";
import "./Footer.scss";

const socials = [
  { icon: <FaLinkedinIn />, href: "https://www.linkedin.com/in/usama-sajid-dev", label: "LinkedIn" },
  { icon: <FaFacebookF />, href: "https://www.facebook.com/usama.sajid.4862", label: "Facebook" },
  { icon: <BsInstagram />, href: "https://www.instagram.com/usamasajid4/", label: "Instagram" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBMIT_MIN_MS = 700;

const wait = (duration) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

const validate = ({ name, email, message }) => {
  const errors = {};
  if (!name.trim()) errors.name = "Name is required.";
  else if (name.trim().length < 2) errors.name = "Name must be at least 2 characters.";

  if (!email.trim()) errors.email = "Email is required.";
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address.";

  if (!message.trim()) errors.message = "Message is required.";
  else if (message.trim().length < 10) errors.message = "Message must be at least 10 characters.";

  return errors;
};

const Footer = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const { name, email, message } = formData;

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;
    const updated = { ...formData, [fieldName]: value };
    setFormData(updated);

    if (touched[fieldName]) {
      const nextErrors = validate(updated);
      setErrors((prev) => ({ ...prev, [fieldName]: nextErrors[fieldName] }));
    }
  };

  const handleBlur = (event) => {
    const { name: fieldName } = event.target;
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const nextErrors = validate(formData);
    setErrors((prev) => ({ ...prev, [fieldName]: nextErrors[fieldName] }));
  };

  const closeSuccessModal = () => setShowSuccessModal(false);

  useEffect(() => {
    if (!showSuccessModal) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSuccessModal();
    };

    document.body.classList.add("modal-open");
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showSuccessModal]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched({ name: true, email: true, message: true });
    const nextErrors = validate(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const submitStartedAt = Date.now();
    setLoading(true);

    try {
      await client.create({ _type: "contact", name, email, message });

      emailjs
        .send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          { from_name: name, from_email: email, name, email, message },
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        )
        .catch(console.warn);

      await wait(Math.max(0, SUBMIT_MIN_MS - (Date.now() - submitStartedAt)));

      setLoading(false);
      setFormData({ name: "", email: "", message: "" });
      setTouched({});
      setErrors({});
      window.setTimeout(() => setShowSuccessModal(true), 90);
    } catch (error) {
      console.log(error);
      await wait(Math.max(0, SUBMIT_MIN_MS - (Date.now() - submitStartedAt)));
      setLoading(false);
    }
  };

  return (
    <div className="ftr">
      <motion.div
        className="ftr__intro"
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ once: true }}
      >
        <span className="app__kicker">
          <em>05</em>Contact
        </span>
        <h2>
          Tell me about your <em>next interface.</em>
        </h2>
        <p>
          Send a short note about what you need. I will reply within a day with
          a clear next step.
        </p>

        <div className="ftr__channels">
          <a href="mailto:usamasajid8@gmail.com" className="ftr__channel">
            <i>
              <FiMail />
            </i>
            <span>
              <small>Email</small>
              usamasajid8@gmail.com
            </span>
            <FiArrowRight className="ftr__channel-arrow" aria-hidden="true" />
          </a>
          <a href="tel:+971529729709" className="ftr__channel">
            <i>
              <FiPhone />
            </i>
            <span>
              <small>Phone</small>
              +971 52 972 9709
            </span>
            <FiArrowRight className="ftr__channel-arrow" aria-hidden="true" />
          </a>
        </div>

        <div className="ftr__socials">
          <span className="ftr__socials-label">Elsewhere</span>
          <div>
            {socials.map(({ icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                {icon}
              </a>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.form
        className="ftr__panel"
        onSubmit={handleSubmit}
        noValidate
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ once: true }}
      >
        <div className="ftr__panel-head">
          <span>Direct message</span>
          <h3>Send a quick inquiry.</h3>
          <p>Name, email, and a short message are enough.</p>
        </div>

        <div className="ftr__form">
          <div className="ftr__row">
            <div className="ftr__field">
              <label htmlFor="ftr-name">Name</label>
              <input
                id="ftr-name"
                type="text"
                name="name"
                value={name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Your name"
                autoComplete="name"
                className={errors.name && touched.name ? "is-invalid" : ""}
              />
              {errors.name && touched.name && <span className="ftr__error">{errors.name}</span>}
            </div>

            <div className="ftr__field">
              <label htmlFor="ftr-email">Email</label>
              <input
                id="ftr-email"
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="you@example.com"
                autoComplete="email"
                className={errors.email && touched.email ? "is-invalid" : ""}
              />
              {errors.email && touched.email && <span className="ftr__error">{errors.email}</span>}
            </div>
          </div>

          <div className="ftr__field">
            <label htmlFor="ftr-msg">Project details</label>
            <textarea
              id="ftr-msg"
              name="message"
              value={message}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Briefly describe what you need."
              rows={5}
              className={errors.message && touched.message ? "is-invalid" : ""}
            />
            {errors.message && touched.message && <span className="ftr__error">{errors.message}</span>}
          </div>

          <button type="submit" className="btn btn--dark ftr__submit" disabled={loading}>
            {loading ? (
              <>
                <span className="ftr__spinner" aria-hidden="true" />
                Sending&hellip;
              </>
            ) : (
              <>
                Send inquiry
                <FiArrowRight />
              </>
            )}
          </button>

          <span className="ftr__privacy">
            Replies within 24h &mdash; your details are never shared.
          </span>
        </div>
      </motion.form>

      {createPortal(
        <AnimatePresence>
          {showSuccessModal && (
            <motion.div
              className="ftr__modal-backdrop"
              role="presentation"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onMouseDown={closeSuccessModal}
            >
              <motion.div
                className="ftr__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ftr-success-title"
                initial={{ opacity: 0, y: 26, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.97 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="ftr__receipt-bar">
                  <span className="ftr__receipt-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <strong>inquiry &mdash; confirmation</strong>
                  <button
                    type="button"
                    className="ftr__receipt-close"
                    onClick={closeSuccessModal}
                    aria-label="Close confirmation"
                  >
                    <FiX />
                  </button>
                </div>

                <div className="ftr__receipt-body">
                  <span className="ftr__receipt-status">
                    <FiCheckCircle aria-hidden="true" />
                    Sent successfully
                  </span>
                  <h3 id="ftr-success-title">Your inquiry is received.</h3>
                  <p>I will review your message and reply with the next step.</p>
                </div>

                <div className="ftr__receipt-foot">
                  <span>Thank you for reaching out.</span>
                  <button type="button" className="btn btn--dark" onClick={closeSuccessModal}>
                    Done
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default AppWrap(
  MotionWrap(Footer, "app__footer"),
  "contact",
  "app__band--stone"
);
